import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MediaCard from "@/components/feature/MediaCard";
import { mediaList, type MediaType } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import { formatDate, searchContent } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

type ResultType = "all" | MediaType | "news";

const filters: { key: ResultType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
  { key: "news", label: "资讯" },
];

const PAGE_SIZE = 12;

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") ?? "";
  const [input, setInput] = useState(q);
  const [active, setActive] = useState<ResultType>("all");

  useEffect(() => {
    setInput(q);
    setActive("all");
  }, [q]);

  usePageMeta({
    title: q ? `搜索“${q}” - po18娱乐内容聚合平台` : "搜索 - po18娱乐内容聚合平台",
    description: q
      ? `po18 中关于“${q}”的搜索结果，涵盖电影、电视剧、综艺与娱乐资讯。`
      : "在 po18 搜索影视内容与娱乐资讯。",
    keywords: q ? `po18,${q},搜索,影视推荐` : "po18,搜索",
  });

  const { media, news } = useMemo(() => searchContent(q), [q]);

  const filteredMedia = useMemo(() => {
    if (active === "news" || active === "all") return media;
    return media.filter((m) => m.type === active);
  }, [media, active]);

  const showNews = active === "all" || active === "news";
  const filteredNews = showNews ? news : [];

  const totalShown =
    active === "news" ? filteredNews.length
    : active === "all" ? media.length + news.length
    : filteredMedia.length;

  const mediaToShow = active === "news" ? [] : filteredMedia.slice(0, PAGE_SIZE);
  const newsToShow = active === "all" || active === "news" ? filteredNews : [];

  const hotPicks = useMemo(
    () => [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 6),
    [],
  );

  const hotNews = useMemo(() => newsList.slice(0, 3), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const hasQuery = q.trim().length > 0;
  const noResult = hasQuery && totalShown === 0;

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6">
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium">搜索</span>
        </nav>

        {/* 搜索框 */}
        <div className="mt-4 rounded-lg bg-gradient-to-r from-primary-50 to-background-100 border border-background-200/70 p-5 md:p-7">
          <h1 className="text-xl md:text-2xl font-black text-foreground-950">
            {hasQuery ? (
              <>
                搜索“<span className="text-primary-600">{q}</span>”的结果
              </>
            ) : (
              "搜索影视与资讯"
            )}
          </h1>
          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 h-12 flex items-center gap-2 px-4 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-300 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、主演或资讯关键词…"
                className="flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          {hasQuery && (
            <p className="mt-3 text-sm text-foreground-500">
              共找到 <span className="text-primary-600 font-semibold">{totalShown}</span> 条结果
            </p>
          )}
        </div>

        {hasQuery && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {filters.map((f) => {
              const isActive = active === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActive(f.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {noResult ? (
          <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 py-16 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
              <i className="ri-search-eye-line" />
            </span>
            <h2 className="mt-5 text-lg font-bold text-foreground-900">
              未找到与“{q}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或看看下面的热门推荐。
            </p>
          </div>
        ) : hasQuery ? (
          <div className="mt-6 space-y-8">
            {mediaToShow.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
                  <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-film-line" />
                  </span>
                  影视内容（{filteredMedia.length}）
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {mediaToShow.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
                {active !== "news" && filteredMedia.length > PAGE_SIZE && (
                  <p className="mt-4 text-sm text-foreground-500 text-center">
                    仅展示前 {PAGE_SIZE} 条，请使用上方筛选缩小范围。
                  </p>
                )}
              </section>
            )}

            {newsToShow.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
                  <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center">
                    <i className="ri-newspaper-line" />
                  </span>
                  娱乐资讯（{newsToShow.length}）
                </h2>
                <div className="space-y-3">
                  {newsToShow.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex gap-4 cursor-pointer rounded-lg bg-background-100 border border-background-200/70 p-4 hover:border-primary-200 transition-colors"
                    >
                      <div className="w-24 sm:w-32 shrink-0 aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.image}
                          alt={`${item.title} 配图`}
                          title={`${item.title} - po18 娱乐资讯`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium">
                            {item.category}
                          </span>
                          <span className="text-foreground-400">{formatDate(item.date)}</span>
                        </div>
                        <h3 className="mt-2 text-sm md:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs md:text-sm text-foreground-600 line-clamp-2 hidden sm:block">
                          {item.summary}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="mt-8">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
              <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className="ri-fire-line" />
              </span>
              热门推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {hotPicks.map((item) => (
                <MediaCard key={item.id} item={item} showType={false} />
              ))}
            </div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mt-10 mb-4">
              <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center">
                <i className="ri-newspaper-line" />
              </span>
              最新资讯
            </h2>
            <div className="space-y-3">
              {hotNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-center gap-3 cursor-pointer rounded-lg bg-background-100 border border-background-200/70 p-4 hover:border-primary-200 transition-colors"
                >
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium shrink-0">
                    {item.category}
                  </span>
                  <p className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}